import { QueryClient, QueryFunction } from '@tanstack/react-query';

// Request interface for the new object-based API request
interface ApiRequestConfig {
  url: string;
  method: string;
  data?: unknown;
  headers?: Record<string, string>;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

// Function overloads for apiRequest

export async function apiRequest<T = any>(
  method: string,
  url: string,
  data?: unknown | undefined
): Promise<T>;

// eslint-disable-next-line no-redeclare
export async function apiRequest<T = any>(config: ApiRequestConfig): Promise<T>;

// Implementation that handles both function signatures
// eslint-disable-next-line no-redeclare
export async function apiRequest<T = any>(
  methodOrConfig: string | ApiRequestConfig,
  url?: string,
  data?: unknown
): Promise<T> {
  let finalUrl: string;
  let finalMethod: string;
  let finalData: unknown | undefined;
  let finalHeaders: Record<string, string> = {};

  // Determine which function signature was used
  if (typeof methodOrConfig === 'string') {
    // Legacy signature (method, url, data)
    finalMethod = methodOrConfig;
    finalUrl = url as string;
    finalData = data;
    if (finalData) {
      finalHeaders = { 'Content-Type': 'application/json' };
    }
  } else {
    // New object-based signature
    finalMethod = methodOrConfig.method;
    finalUrl = methodOrConfig.url;
    finalData = methodOrConfig.data;
    finalHeaders = methodOrConfig.headers || {};
    if (finalData && !finalHeaders['Content-Type']) {
      finalHeaders['Content-Type'] = 'application/json';
    }
  }

  const res = await fetch(finalUrl, {
    method: finalMethod,
    headers: finalHeaders,
    body: finalData ? JSON.stringify(finalData) : undefined,
    credentials: 'include',
  });

  await throwIfResNotOk(res);

  // For DELETE requests that return 204 No Content or empty response
  if (finalMethod === 'DELETE' && res.status === 204) {
    return {} as T;
  }

  // Check if response has content
  const text = await res.text();
  if (!text) {
    return {} as T;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text as T;
  }
}

type UnauthorizedBehavior = 'returnNull' | 'throw';
export const getQueryFn: <T>(options: { on401: UnauthorizedBehavior }) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    try {
      // Make sure queryKey[0] exists and is a string
      if (!queryKey || !queryKey[0] || typeof queryKey[0] !== 'string') {
        console.error('Invalid queryKey:', queryKey);
        // Instead of throwing an error, return null to prevent app from crashing
        return null;
      }

      const res = await fetch(queryKey[0] as string, {
        credentials: 'include',
      });

      if (unauthorizedBehavior === 'returnNull' && res.status === 401) {
        return null;
      }

      await throwIfResNotOk(res);
      return await res.json();
    } catch (error) {
      console.error('Query fetch error:', error);
      if (error instanceof Error && error.message.includes('401')) {
        if (unauthorizedBehavior === 'returnNull') {
          return null;
        }
      }
      // Re-throw the error so React Query can handle it properly
      throw error;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: 'throw' }),
      refetchInterval: false,
      // Smart refetch on window focus with improved logic
      refetchOnWindowFocus: (query) => {
        // Skip refetch for auth queries to prevent loops
        if (query.queryKey[0] === '/api/user' || query.queryKey[0] === '/api/dev-login') {
          return false;
        }
        // Skip refetch for site survey queries to prevent duplicate notifications
        if (query.queryKey.includes('site-survey') || query.queryKey.includes('site-surveys')) {
          return false;
        }
        // Only refetch if data is older than 2 minutes
        const lastFetchTime = query.state.dataUpdatedAt;
        const twoMinutesAgo = Date.now() - 2 * 60 * 1000;
        return lastFetchTime < twoMinutesAgo;
      },
      // Increased stale time to reduce unnecessary refetches
      staleTime: 30 * 1000, // Consider data fresh for 30 seconds (was 5 seconds)
      gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour (was 30 minutes)
      retry: (failureCount, error) => {
        // Don't retry on 401 errors
        if (error && 'message' in error && error.message.includes('401')) {
          return false;
        }
        // Don't retry on 404 errors
        if (error && 'message' in error && error.message.includes('404')) {
          return false;
        }
        // Don't retry on 403 errors
        if (error && 'message' in error && error.message.includes('403')) {
          return false;
        }
        // Retry with exponential backoff
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Network mode to handle offline scenarios
      networkMode: 'offlineFirst',
    },
    mutations: {
      retry: false,
      // Optimistic updates should be rolled back on error
      onError: (_error, _variables, context) => {
        if (context?.previousData) {
          // Rollback optimistic updates
          return context.previousData;
        }
      },
      // Network mode for mutations
      networkMode: 'online',
    },
  },
});
